import React, { useState } from "react";
import Modal from "../../../shared/modal/Modal";
import { getSettings, setSettings as persistSettings, Settings } from "../../../shared/settings";
import KeyPicker from "../../../shared/key-picker/KeyPicker";
import { useKeyChainFocus } from "../../../shared/useKeyChainFocus";
import { useIsTouch } from "../../../shared/useResponsive";
import { wormColors } from "core/src/core/constants.js";
import styles from "../../settings/settings.module.scss";

interface KeyPlayer {
  id: string;
  name: string;
  colorId: string;
  left: string | null;
  right: string | null;
}

interface Props {
  onClose: () => void;
  // when provided, a Controls section lets the local human(s) rebind their keys
  players?: KeyPlayer[];
  onChangeKey?: (playerId: string, side: "left" | "right", key: string) => void;
}

// The in-game settings dialog: sound, plus (when handed local players) live key
// rebinding. Same store as the Settings page, but as a modal over the match.
export default function SettingsModal({ onClose, players, onChangeKey }: Props) {
  const [s, setS] = useState<Settings>(() => getSettings());
  const [, force] = useState(0); // re-render after an in-place key change
  const { advance, focusToken } = useKeyChainFocus();
  // On touch devices you steer by tapping, so keyboard key bindings are
  // meaningless here - hide the Controls section.
  const touch = useIsTouch();

  function update(patch: Partial<Settings>) {
    const next = { ...s, ...patch };
    setS(next);
    persistSettings(next);
  }

  function changeKey(id: string, side: "left" | "right", key: string) {
    if (onChangeKey) onChangeKey(id, side, key);
    advance(id, side);
    force((n) => n + 1);
  }

  return (
    <Modal
      title="Settings"
      onClose={onClose}
      footer={<button className="app-btn app-btn-primary" onClick={onClose}>Done</button>}
    >
      <div className={styles.settingsGroup}>
        <h2 className={styles.settingsH}>Sound</h2>
        <label className={styles.settingsRow}>
          <span>Sound</span>
          <input className={styles.settingsToggle} type="checkbox" checked={!s.muted} onChange={(e) => update({ muted: !e.target.checked })} />
        </label>
        <label className={styles.settingsRow}>
          <span>Music volume</span>
          <input
            type="range"
            min={0}
            max={100}
            value={s.volume}
            disabled={s.muted}
            onChange={(e) => update({ volume: Number(e.target.value) })}
          />
          <span className={`${styles.settingsVal} app-muted`}>{s.muted ? "off" : s.volume + "%"}</span>
        </label>
      </div>

      {!touch && players && players.length > 0 && onChangeKey && (
        <div className={styles.settingsGroup}>
          <h2 className={styles.settingsH}>Controls</h2>
          {players.map((p) => (
            <div className={styles.settingsRow} key={p.id}>
              <span style={{ color: wormColors[p.colorId] }}>{p.name}</span>
              <div className={styles.settingsKeys}>
                <KeyPicker currentKey={p.left} onKeyPicked={(k: string) => changeKey(p.id, "left", k)} autoSelectToken={focusToken(p.id, "left")} />
                <KeyPicker currentKey={p.right} onKeyPicked={(k: string) => changeKey(p.id, "right", k)} autoSelectToken={focusToken(p.id, "right")} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
